import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';

import Echo from '@/echo';
import { adminClient } from '@/store/apollo';
import ChannelAccountApi from '@/extensions/communication/store/api/channelAccount';
import ConversationApi from '@/extensions/communication/store/api/conversation';
import UnreadConversationsCountGql from '@/extensions/communication/store/graphql/queries/UnreadConversationsCount.gql';
import { useSetupStore } from '@/stores';

// The ONE store of the communication extension: channel accounts (mailboxes /
// WhatsApp connections) AND conversations/messages live here together — they are
// one feature, and the composer needs both anyway.
export const useCommunicationStore = defineStore('communication', {
    state: () => ({
        // ── channel accounts ────────────────────────────────────────────────
        loadingAccounts: true,
        accounts: [],

        // ── conversations ───────────────────────────────────────────────────
        loading: true,
        conversationsPaginated: {
            variables: {
                order: 'last_message_at',
                direction: 'desc',
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            items: []
        },
        activeConversation: null,
        loadingConversation: false,
        // Total unread conversations for the active CMS — drives every unread pill.
        unreadCount: 0,
        // POS-scoped unread total — drives the POS Messages tab badge (isolated).
        posUnreadCount: 0,
        // Bumped on each realtime event so list views can refetch their own data.
        realtimeTick: 0,
        realtimeCmsId: null,
        realtimeGeneration: 0
    }),

    getters: {
        // Active channel filter key ('email' / 'whatsapp') or null for "all".
        activeChannelFilter: (state) => _.get(state.conversationsPaginated.filters, 'channel.in.0', null),
        unassignedOnly: (state) => _.get(state.conversationsPaginated.filters, 'assignment.in.0', null) === 'unassigned'
    },

    actions: {
        // ── channel accounts ────────────────────────────────────────────────

        fetchAccounts() {
            const cms = useSetupStore().activeCms;
            if (!cms) return Promise.resolve([]);

            this.loadingAccounts = true;
            return ChannelAccountApi.fetchList(cms.id)
                .then(accounts => {
                    this.accounts = _.cloneDeep(accounts) || [];
                    return this.accounts;
                })
                .finally(() => {
                    this.loadingAccounts = false;
                });
        },

        saveAccount(account) {
            const cms = useSetupStore().activeCms;
            return ChannelAccountApi.save(cms.id, account);
        },

        validateAccount(id) {
            return ChannelAccountApi.validate(id);
        },

        deleteAccount(id) {
            return ChannelAccountApi.delete(id);
        },

        oauthUrl(id) {
            return ChannelAccountApi.oauthUrl(id);
        },

        whatsappSignupConfig() {
            const cms = useSetupStore().activeCms;
            if (!cms) return Promise.resolve(null);
            return ChannelAccountApi.whatsappSignupConfig(cms.id);
        },

        connectWhatsApp(payload) {
            const cms = useSetupStore().activeCms;
            return ChannelAccountApi.connectWhatsApp(cms.id, payload);
        },

        // ── conversations ───────────────────────────────────────────────────

        addPaginatedToState(data) {
            this.conversationsPaginated.items = data.items;
            this.conversationsPaginated.pageInfo = _.cloneDeep(data.pageInfo);
        },

        defineCurrentPage(currentPage) {
            this.conversationsPaginated.pageInfo.currentPage = currentPage;
        },

        defineItemsPerPage(perPage) {
            this.conversationsPaginated.pageInfo.perPage = perPage;
        },

        setFilter(key, value) {
            if (value === null || value === undefined) {
                Vue.delete(this.conversationsPaginated.filters, key);
            } else {
                Vue.set(this.conversationsPaginated.filters, key, value);
            }
            this.conversationsPaginated.pageInfo.currentPage = 1;
        },

        // chip selection: null = all, otherwise the channel option key
        setChannelFilter(channelKey) {
            if (!channelKey) {
                this.setFilter('channel', null);
            } else {
                this.setFilter('channel', { key: 'channel', in: [channelKey] });
            }
        },

        setAssignmentFilter(assignmentKey) {
            if (!assignmentKey) {
                this.setFilter('assignment', null);
            } else {
                this.setFilter('assignment', { key: 'assignment', in: [assignmentKey] });
            }
        },

        setSearch(term) {
            if (!term) {
                this.setFilter('subject', null);
            } else {
                this.setFilter('subject', { key: 'subject', like: term });
            }
        },

        fetchConversations() {
            const cms = useSetupStore().activeCms;
            if (!cms) return Promise.resolve();

            this.loading = true;

            // Always scope to the active CMS.
            Vue.set(this.conversationsPaginated.filters, 'cms', { key: 'cms', match: cms.id });

            return ConversationApi.fetchConversationsPaginated(this.conversationsPaginated)
                .then(data => this.addPaginatedToState(data))
                .finally(() => {
                    this.loading = false;
                });
        },

        // `quiet` re-pulls the thread WITHOUT the blocking overlay. Opening a conversation is
        // a user-initiated load and should show the spinner; a background refresh (a delivery
        // receipt ticking sent -> delivered -> read, an inbound message arriving) must not
        // blank the chat the user is reading — that produced a visible flicker on every tick.
        fetchConversation(id, { quiet = false } = {}) {
            if (!quiet) this.loadingConversation = true;
            return ConversationApi.fetchConversation(id)
                .then(conversation => {
                    this.activeConversation = _.cloneDeep(conversation);
                    return conversation;
                })
                .finally(() => {
                    if (!quiet) this.loadingConversation = false;
                });
        },

        clearActiveConversation() {
            this.activeConversation = null;
        },

        sendMessage({ body, subject = null, attachments = [], templateName = null, templatePayload = null, templateComponents = null, cc = [], bcc = [] }) {
            const conversation = this.activeConversation;
            if (!conversation) return Promise.reject(new Error('No active conversation'));

            return ConversationApi.sendMessage({
                conversation_id: conversation.id,
                subject,
                body,
                cc,
                bcc,
                attachments,
                template_name: templateName,
                templatePayload: templatePayload,
                templateComponents: templateComponents
            }).then(() => this.refreshAfterMutation(conversation.id));
        },

        // Compose a brand-new conversation (Neue Nachricht / forward). Returns the
        // new conversation so the caller can open it.
        startConversation(payload) {
            return ConversationApi.startConversation(payload).then(conversation => {
                this.fetchConversations();
                return conversation;
            });
        },

        createNote(body) {
            const conversation = this.activeConversation;
            if (!conversation) return Promise.reject(new Error('No active conversation'));

            return ConversationApi.createNote(conversation.id, body)
                .then(() => this.refreshAfterMutation(conversation.id));
        },

        deleteNote(messageId) {
            const conversation = this.activeConversation;
            return ConversationApi.deleteNote(messageId)
                .then(() => conversation ? this.refreshAfterMutation(conversation.id) : null);
        },

        markRead(id, read = true) {
            return ConversationApi.markRead(id, read).then(updated => {
                const count = updated ? updated.unread_count : (read ? 0 : 1);
                const item = _.find(this.conversationsPaginated.items, { id });
                if (item) item.unread_count = count;
                if (this.activeConversation && this.activeConversation.id === id) {
                    this.activeConversation.unread_count = count;
                    // Keep per-message is_read consistent with the thread-level action, so
                    // the message toggles + the derived count never go stale (this is what
                    // makes the auto-mark-read on open not leave messages looking unread).
                    this.applyReadStateToMessages(this.activeConversation, read);
                }
                // Update my own badges immediately (the broadcast handles other admins).
                // onUnreadChanged, not fetchUnreadCount: the POS tab pill is a SEPARATE
                // counter that only refreshes off the tick, so calling the narrow one
                // left it stale after a local toggle.
                this.onUnreadChanged();
            });
        },

        // Set a manual subject/label on a conversation (the non-AI topic for WhatsApp
        // threads); patch the open thread + list row so it retitles immediately.
        setSubject(id, subject) {
            return ConversationApi.setSubject(id, subject).then(updated => {
                const value = updated ? updated.subject : (subject || null);
                const item = _.find(this.conversationsPaginated.items, { id });
                if (item) item.subject = value;
                if (this.activeConversation && this.activeConversation.id === id) {
                    this.activeConversation.subject = value;
                }
                return value;
            });
        },

        // Mirror the backend's thread semantics onto the open thread's messages:
        // mark read = every inbound message read; mark unread = only the LATEST inbound.
        applyReadStateToMessages(conversation, read) {
            const inbound = (conversation.messages || []).filter(m => m.direction === 'inbound');
            if (!inbound.length) return;
            if (read) {
                inbound.forEach(message => { message.is_read = true; });
            } else {
                const latest = inbound.reduce((a, b) => (b.id > a.id ? b : a));
                latest.is_read = false;
            }
        },

        // Toggle a single message's read state; patch it in the open thread and RE-DERIVE
        // the conversation's unread_count from per-message state (inbound + not read),
        // mirroring the backend's derived model. Returns the new count.
        markMessageRead(id, read = true) {
            return ConversationApi.markMessageRead(id, read).then(updated => {
                const conversation = this.activeConversation;
                let count = conversation ? (conversation.unread_count || 0) : 0;
                if (conversation && conversation.messages) {
                    const message = _.find(conversation.messages, { id });
                    if (message) message.is_read = updated ? updated.is_read : read;
                    count = conversation.messages.filter(m => m.direction === 'inbound' && !m.is_read).length;
                    conversation.unread_count = count;
                    const item = _.find(this.conversationsPaginated.items, { id: conversation.id });
                    if (item) item.unread_count = count;
                }
                this.onUnreadChanged();
                return count;
            });
        },

        // Total unread count for the active CMS — the source of truth for the pills.
        fetchUnreadCount() {
            const cms = useSetupStore().activeCms;
            if (!cms || !cms.id) {
                this.unreadCount = 0;
                return Promise.resolve();
            }
            return adminClient.query({
                query: UnreadConversationsCountGql,
                variables: { cms_id: parseInt(cms.id, 10) },
                fetchPolicy: 'network-only'
            })
                .then(({ data }) => { this.unreadCount = data.EX_COMM_UnreadConversationsCount || 0; })
                .catch(() => { /* the badge is best-effort */ });
        },

        // POS-scoped unread total for the POS Messages tab badge (strict isolation).
        fetchPosUnreadCount(pointOfSaleId) {
            const cms = useSetupStore().activeCms;
            if (!cms || !cms.id || !pointOfSaleId) {
                this.posUnreadCount = 0;
                return Promise.resolve();
            }
            return adminClient.query({
                query: UnreadConversationsCountGql,
                variables: { cms_id: parseInt(cms.id, 10), point_of_sale_id: parseInt(pointOfSaleId, 10) },
                fetchPolicy: 'network-only'
            })
                .then(({ data }) => { this.posUnreadCount = data.EX_COMM_UnreadConversationsCount || 0; })
                .catch(() => { /* best-effort */ });
        },

        // Subscribe to the CMS broadcast channel so every admin's unread pills update
        // live on inbound mail and on read/unread toggles. Idempotent per CMS.
        subscribeRealtime() {
            const cms = useSetupStore().activeCms;
            if (!cms || !cms.id) return;
            // Same CMS on a rebuilt socket (mimic in/out) is not subscribed.
            if (this.realtimeCmsId === cms.id && this.realtimeGeneration === Echo.generation) {
                this.fetchUnreadCount();
                return;
            }
            this.unsubscribeRealtime();
            if (!Echo.client) Echo.initialize();
            if (!Echo.client) return;

            this.realtimeCmsId = cms.id;
            this.realtimeGeneration = Echo.generation;
            const channel = Echo.client.private(`cms.${cms.id}`);
            // A new inbound message needs the open thread re-pulled (it adds a message);
            // an unread-count change does NOT (no new message, and the toggling client
            // already patched its own read state) — so it uses a lighter handler that
            // avoids re-fetching the open conversation on every mark read/unread.
            channel.listen('.communication:message-received', () => this.onRealtimeChange());
            channel.listen('.communication:unread-changed', () => this.onUnreadChanged());
            // Outbound delivery lifecycle (queued → sent / delivered / failed):
            // refresh the open thread so the message card reflects the new status
            // live instead of staying on "queued" until a manual refresh.
            channel.listen('.communication:message-status', payload => this.onMessageStatus(payload));
            // Live open/click engagement — same event the purchase form uses.
            channel.listen('.sent-email:engagement-updated', snapshot => this.patchSentEmail(snapshot));

            this.fetchUnreadCount();
        },

        // Patch a tracking snapshot onto the matching message's sent_email in place
        // (no refetch) so the engagement strip updates live on open/click.
        patchSentEmail(snapshot) {
            if (!snapshot || !snapshot.id) return;
            const messages = _.get(this.activeConversation, 'messages') || [];
            messages.forEach(message => {
                // GraphQL serializes the id as a String; the broadcast sends an Int —
                // coerce both so the snapshot actually merges.
                if (message.sentEmail && String(message.sentEmail.id) === String(snapshot.id)) {
                    message.sentEmail = { ...message.sentEmail, ...snapshot };
                }
            });
        },

        // An unread-count change (mark read/unread — here or from another admin) adds no
        // message, so don't re-pull the open conversation. Bump the tick (the list rows
        // re-sync via softRefresh) and refresh the badges. This is what removes the
        // redundant self-refetch cascade on every toggle.
        onUnreadChanged() {
            this.realtimeTick++;
            this.fetchUnreadCount();
        },

        onRealtimeChange() {
            this.realtimeTick++;
            this.fetchUnreadCount();
            // Keep the global conversation list in sync when it is loaded.
            if (this.conversationsPaginated.items.length) this.fetchConversations();
            // A new inbound message may belong to the OPEN conversation — refresh it
            // so the message appears live. Without this the unread pill updated but the
            // open thread did not, and the new email was only reachable after leaving
            // and re-opening the conversation.
            const activeId = _.get(this.activeConversation, 'id');
            if (activeId) this.fetchConversation(activeId, { quiet: true });
        },

        // An outbound message's status changed (sent/delivered/failed). Re-pull the
        // open conversation so its message card shows the new status; also nudge the
        // list (sort key / row state).
        onMessageStatus(payload) {
            this.realtimeTick++;
            const activeId = _.get(this.activeConversation, 'id');
            if (activeId && payload && Number(payload.conversation_id) === Number(activeId)) {
                this.fetchConversation(activeId, { quiet: true });
            }
        },

        unsubscribeRealtime() {
            // Tear down ONLY this store's own listeners — never `leave()` the channel,
            // which would also kill the presence store's `.presence:communication`
            // listener that shares it. Mirror presence.js's stopListening teardown.
            if (this.realtimeCmsId && Echo.client) {
                try {
                    const channel = Echo.client.private(`cms.${this.realtimeCmsId}`);
                    channel.stopListening('.communication:message-received');
                    channel.stopListening('.communication:unread-changed');
                    channel.stopListening('.communication:message-status');
                    channel.stopListening('.sent-email:engagement-updated');
                } catch (error) { /* noop */ }
            }
            this.realtimeCmsId = null;
        },

        // Clear the unread badge total via an action (no direct state mutation outside).
        resetUnread() {
            this.unreadCount = 0;
        },

        closeConversation(id, reopen = false) {
            return ConversationApi.close(id, reopen).then(() => this.refreshAfterMutation(id));
        },

        deleteConversation(id) {
            return ConversationApi.deleteConversation(id).then(() => {
                if (this.activeConversation && this.activeConversation.id === id) {
                    this.clearActiveConversation();
                }
                this.onUnreadChanged();
            });
        },

        markSpam(id, unmark = false) {
            return ConversationApi.markSpam(id, unmark).then(() => {
                this.fetchUnreadCount();
                return this.fetchConversation(id);
            });
        },

        // Every triage action from the LIST goes through here - a single row is a
        // selection of one. The thread header keeps its single-id actions.
        bulkAction(ids, action) {
            return ConversationApi.bulkAction(ids, action).then(() => {
                // `ids` comes straight from the caller's selection state (row checkboxes /
                // Object.keys of a selection map) and may hold strings; activeConversation.id
                // is always a Number - coerce both sides so the match is not lost, mirroring
                // the sentEmail.id coercion above.
                // Only actions that REMOVE the conversation from view should close the
                // open thread. Marking the open conversation read/unread must leave it
                // on screen, or the thread vanishes out from under the reader.
                const removesFromView = ['delete', 'spam'].includes(action);
                const activeIsIncluded = this.activeConversation
                    && ids.map(String).includes(String(this.activeConversation.id));
                if (removesFromView && activeIsIncluded) {
                    this.clearActiveConversation();
                }
                this.onUnreadChanged();
            });
        },

        // mode: 'sync' for a single conversation, 'add' / 'remove' across a selection.
        setIndicators(ids, indicatorIds, mode) {
            return ConversationApi.setIndicators(ids, indicatorIds, mode).then(updated => {
                updated.forEach(fresh => {
                    const item = _.find(this.conversationsPaginated.items, { id: fresh.id });
                    if (item) item.indicators = fresh.indicators;
                    if (this.activeConversation && this.activeConversation.id === fresh.id) {
                        this.activeConversation.indicators = fresh.indicators;
                    }
                });
                return updated;
            });
        },

        // Re-queue an existing outbound message (no new message instance).
        resendMessage(messageId) {
            return ConversationApi.resendMessage(messageId).then(conversation => {
                if (conversation && conversation.id) return this.fetchConversation(conversation.id);
            });
        },

        linkToMember(conversationID, memberID) {
            return ConversationApi.linkToMember(conversationID, memberID)
                .then(() => this.refreshAfterMutation(conversationID));
        },

        linkToPurchase(conversationID, purchaseID) {
            return ConversationApi.linkToPurchase(conversationID, purchaseID)
                .then(() => this.refreshAfterMutation(conversationID));
        },

        refreshAfterMutation(conversationID) {
            return Promise.all([
                this.fetchConversation(conversationID),
                this.fetchConversations()
            ]);
        }
    }
});
